"use client";

import { useEffect, useState } from "react";
import { fetchRegionStates, updateRegionStates } from "@/lib/queries";
import type { RegionState } from "@/lib/types";

// Configures the fixed state list behind the listings page's "region"
// quick-select button on the State filter (see MultiSelect's
// regionOptions prop). Local edit-then-save, same pattern as
// DefaultsSection, rather than saving on every checkbox click --
// avoids a network round-trip per click across 50 checkboxes.
export function RegionStatesSection() {
  const [states, setStates] = useState<RegionState[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchRegionStates()
      .then((rows) => {
        setStates(rows);
        setDrafts(Object.fromEntries(rows.map((r) => [r.state, r.included])));
      })
      .catch((e) => setError(e.message ?? String(e)));
  }, []);

  const toggle = (state: string) => {
    setDrafts((prev) => ({ ...prev, [state]: !prev[state] }));
    setSaved(false);
  };

  const isDirty =
    states != null && states.some((s) => drafts[s.state] !== s.included);

  const handleSave = async () => {
    if (!states) return;
    setSaving(true);
    setError(null);
    try {
      const updated = states.map((s) => ({ state: s.state, included: drafts[s.state] ?? s.included }));
      await updateRegionStates(updated);
      setStates(updated);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">Region states</h1>
      <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
        States included in the region quick-select button on the listings page&apos;s State filter.
      </p>

      {error && (
        <div
          className="mt-4 rounded-md border px-4 py-3 text-sm"
          style={{ borderColor: "var(--color-negative)", backgroundColor: "var(--color-negative-muted)", color: "var(--color-negative)" }}
        >
          {error}
        </div>
      )}

      {!states && !error && (
        <div className="py-16 text-center text-sm text-[var(--color-text-secondary)]">Loading…</div>
      )}

      {states && (
        <>
          <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
            {states.map((s) => (
              <label
                key={s.state}
                className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-surface-1)]"
              >
                <input
                  type="checkbox"
                  checked={drafts[s.state] ?? false}
                  onChange={() => toggle(s.state)}
                  className="accent-[var(--color-accent)]"
                />
                {s.state}
              </label>
            ))}
          </div>

          <div className="mt-5 flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={!isDirty || saving}
              className="rounded-md border px-4 py-1.5 text-sm font-medium disabled:opacity-50"
              style={{ borderColor: "var(--color-accent)", color: "var(--color-accent-strong)" }}
            >
              {saving ? "Saving…" : "Save"}
            </button>
            {saved && !isDirty && (
              <span className="text-xs text-[var(--color-text-tertiary)]">Saved.</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
